import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Modal from '../components/Modal';
import PriceListForm from '../components/PriceListForm';
import { apiService } from '../services/api';
import { Plan, PriceListGroup, PriceListGroupUpsert, PricingHeader, Server } from '../types';
import { createPaginationSearchParams, getPageFromSearchParams } from '../utils/pagination';

const pageSize = 10;

const PriceLists: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [priceListGroups, setPriceListGroups] = useState<PriceListGroup[]>([]);
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPriceListGroup, setEditingPriceListGroup] = useState<PriceListGroup | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false); // New state for inactive filter

  const [currentPage, setCurrentPage] = useState(() => getPageFromSearchParams(searchParams));

  useEffect(() => {
    const urlPage = getPageFromSearchParams(searchParams);
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
  }, [searchParams]); 
  useEffect(() => {
    fetchData();
  }, [showInactive, currentPage]); // Re-fetch when showInactive changes

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const [priceListGroupsRes, pricingHeadersRes, plansRes, serversRes] = await Promise.all([
        apiService.getPriceListGroups(showInactive ? null : true),
        apiService.getPricingHeaders(0, 100),
        apiService.getAllPlans(100),
        apiService.servers.getAllServers(500),
      ]);

      setPriceListGroups(Array.isArray(priceListGroupsRes) ? priceListGroupsRes : []);
      setPricingHeaders(Array.isArray(pricingHeadersRes) ? pricingHeadersRes : []);
      setPlans(Array.isArray(plansRes) ? plansRes : []);
      setServers(Array.isArray(serversRes) ? serversRes : []);
    } catch (error: any) {
      console.error('Error fetching pricelist groups:', error);
      setPriceListGroups([]);
      setPricingHeaders([]);
      setPlans([]);
      setServers([]);
      setErrorMessage(error?.response?.data?.detail || 'Failed to load pricelists.');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(priceListGroups.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      setSearchParams(createPaginationSearchParams(totalPages));
    }
  }, [currentPage, totalPages, setSearchParams]);

  const pagedGroups = priceListGroups.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCreate = () => {
    setEditingPriceListGroup(null);
    setErrorMessage(null);
    setShowModal(true);
  };

  const handleEdit = (priceListGroup: PriceListGroup) => {
    setEditingPriceListGroup(priceListGroup);
    setErrorMessage(null);
    setShowModal(true);
  };

  const handleDelete = async (groupId: number) => {
    if (!window.confirm('Are you sure you want to delete this price list and all of its contract periods?')) {
      return;
    }

    try {
      setErrorMessage(null);
      await apiService.deletePriceListGroup(groupId);
      await fetchData();
    } catch (error: any) {
      console.error('Error deleting price list group:', error);
      setErrorMessage(error?.response?.data?.detail || 'Failed to delete pricelist.');
    }
  };

  const handleSubmit = async (data: PriceListGroupUpsert) => {
    try {
      setErrorMessage(null);
      if (editingPriceListGroup) {
        await apiService.updatePriceListGroup(editingPriceListGroup.group_id, data);
      } else {
        await apiService.createPriceListGroup(data);
      }
      setShowModal(false);
      setEditingPriceListGroup(null);
      await fetchData();
    } catch (error: any) {
      console.error('Error saving price list group:', error);
      setErrorMessage(error?.response?.data?.detail || 'Failed to save pricelist.');
    }
  };

  const getPricingHeaderName = (id: number) => {
    const header = pricingHeaders.find((item) => item.pricing_header_id === id);
    return header?.name || `Header ${id}`;
  };

  const getPlanName = (id: number) => {
    const plan = plans.find((item) => item.plan_id === id);
    return plan?.name || `Plan ${id}`;
  };

  const formatCurrency = (amount: number | null | undefined, currency: string) => {
    if (amount === null || amount === undefined) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getYearlyPeriod = (priceListGroup: PriceListGroup) =>
    priceListGroup.periods.find((period) => period.months === 12)
    || priceListGroup.periods.find((period) => period.months === priceListGroup.default_period_months)
    || priceListGroup.periods[0];

  const getServerAccessLabel = (priceListGroup: PriceListGroup) => {
    if (!priceListGroup.servers || priceListGroup.servers.length === 0) {
      return 'All servers';
    }

    return priceListGroup.servers
      .map((server) => `${server.name}${server.is_active ? '' : ' (inactive)'}`)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Price Lists</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create Price List
        </button>
      </div>

      <div className="filter-bar">
        <label>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Show inactive price lists
        </label>
      </div>

      {errorMessage && (
        <div className="card" style={{ marginBottom: '16px', border: '1px solid #dc3545' }}>
          <div className="form-error" style={{ margin: 0 }}>
            {errorMessage}
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Pricing Header</th>
                <th>Plan</th>
                <th>Yearly Monthly Price</th>
                <th>Contract Periods</th>
                <th>Server Access</th>
                <th>Country</th>
                <th>Status</th>
                <th>Valid From</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedGroups.map((priceListGroup) => {
                const yearlyPeriod = getYearlyPeriod(priceListGroup);

                return (
                  <tr key={priceListGroup.group_id}>
                    <td>{priceListGroup.group_id}</td>
                    <td>{priceListGroup.name}</td>
                    <td>{getPricingHeaderName(priceListGroup.pricing_header_id)}</td>
                    <td>{getPlanName(priceListGroup.plan_id)}</td>
                    <td>
                      {yearlyPeriod?.monthly_price !== undefined && yearlyPeriod?.monthly_price !== null
                        ? `${formatCurrency(yearlyPeriod.monthly_price, priceListGroup.currency)} / month`
                        : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {priceListGroup.periods
                          .slice()
                          .sort((left, right) => {
                            if (left.months === 12 && right.months !== 12) {
                              return -1;
                            }
                            if (right.months === 12 && left.months !== 12) {
                              return 1;
                            }
                            return left.months - right.months;
                          })
                          .map((period) => (
                            <div key={period.pricelist_id}>
                              <span
                                className={`badge ${period.is_active ? 'badge-info' : 'badge-danger'}`}
                                style={{ marginRight: '8px' }}
                              >
                                {period.months} month{period.months === 1 ? '' : 's'}
                              </span>
                              <span>
                                {period.monthly_price !== null && period.monthly_price !== undefined
                                  ? `${formatCurrency(period.monthly_price, priceListGroup.currency)} / month`
                                  : '-'}
                              </span>
                            </div>
                          ))}
                      </div>
                    </td>
                    <td>{getServerAccessLabel(priceListGroup)}</td>
                    <td>{priceListGroup.country || '-'}</td>
                    <td>
                      <span className={`badge ${priceListGroup.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {priceListGroup.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(priceListGroup.valid_from).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEdit(priceListGroup)}
                        style={{ marginRight: '5px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(priceListGroup.group_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {pagedGroups.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '24px' }}>
                    No pricelists found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            onClick={() => {
              const newPage = Math.max(currentPage - 1, 1);
              setCurrentPage(newPage);
              setSearchParams(createPaginationSearchParams(newPage));
            }}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => {
              const newPage = Math.min(currentPage + 1, totalPages);
              setCurrentPage(newPage);
              setSearchParams(createPaginationSearchParams(newPage));
            }}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editingPriceListGroup ? 'Edit Price List' : 'Create Price List'}
          onClose={() => {
            setShowModal(false);
            setEditingPriceListGroup(null);
          }}
        >
          <PriceListForm
            initialData={editingPriceListGroup}
            pricingHeaders={pricingHeaders}
            plans={plans}
            servers={servers}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowModal(false);
              setEditingPriceListGroup(null);
            }}
          />
        </Modal>
      )}
      
      <style dangerouslySetInnerHTML={{
        __html: `
        .filter-bar {
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .filter-bar label {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
          color: #666;
          cursor: pointer;
        }

        .filter-bar input[type="checkbox"] {
          cursor: pointer;
        }
        `
      }} />
    </div>
  );
};

export default PriceLists;
